/**
 * Timesheet workflow callables (slice 8.2). The original draft +
 * check-in/out path stays in worker.ts; submit/approve/reject go
 * through callables so the server can classify hours and audit.
 */

import type {
  Job,
  JobQuantity,
  RejectReasonCode,
  ServiceAcceptance,
  ShiftCardMoney,
  Timesheet,
  TimesheetHourBreakdown,
  TimesheetPhoto,
  TimesheetPlace,
  WorkerBreakReport,
  WorkerBreakDecision,
} from '@rendimees24/shared-types';
import { SERVICE_ANSWER_AUDIT_ID } from '@rendimees24/shared-types';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { getDownloadURL, getMetadata, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useEffect, useMemo, useRef, useState } from 'react';

import { needingAction } from '../lib/adminActions';
import { isActiveTimesheet } from '../lib/liveBoard';

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';
import { useNow } from './useNow';

export async function resubmitTimesheet(input: {
  jobId: string;
  timesheetId: string;
  rejectedAt: string;
}): Promise<{ ok: boolean; alreadySubmitted: boolean; status: string }> {
  const { functions } = getFirebase();
  const call = httpsCallable<
    typeof input,
    { ok: boolean; alreadySubmitted: boolean; status: string }
  >(functions, 'resubmitTimesheet');
  return (await call(input)).data;
}

export async function submitTimesheet(input: { jobId: string; timesheetId: string }): Promise<{
  ok: boolean;
  status: Timesheet['status'];
  breakdown?: TimesheetHourBreakdown;
  totalHours?: number;
}> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    typeof input,
    {
      ok: boolean;
      status: Timesheet['status'];
      breakdown?: TimesheetHourBreakdown;
      totalHours?: number;
    }
  >(functions, 'submitTimesheet');
  const r = await callable(input);
  return r.data;
}

export async function approveTimesheet(input: {
  jobId: string;
  timesheetId: string;
  /** EPIC 39 S6 — optional approver comment, appended to reviewComments[]. */
  comment?: string;
  /** M-D4b v4.3 — the client answered "yes, we ended the work early". */
  clientEndedEarly?: boolean;
}): Promise<{ ok: boolean; status: Timesheet['status'] }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<typeof input, { ok: boolean; status: Timesheet['status'] }>(
    functions,
    'approveTimesheet',
  );
  const r = await callable(input);
  return r.data;
}

export async function rejectTimesheet(input: {
  jobId: string;
  timesheetId: string;
  /** Optional free text next to the code (stage 1A, S-T7). */
  reason?: string;
  /** Why the day went back — mandatory for the approver UIs (stage 1A, S-T7). */
  reasonCode?: RejectReasonCode;
}): Promise<{ ok: boolean; status: Timesheet['status'] }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<typeof input, { ok: boolean; status: Timesheet['status'] }>(
    functions,
    'rejectTimesheet',
  );
  const r = await callable(input);
  return r.data;
}

/**
 * EPIC 39 — one-step day completion: stamps the check-out on the worker's
 * open draft entry (optional GPS), applies the statutory auto-lunch, flips
 * the sheet to 'submitted' and notifies the approvers. Replaces the old
 * client-side checkOut + submitTimesheet pair.
 *
 * `endedAt` is when the worker tapped. It matters offline: the queued action
 * may not reach the server for hours, and without it the server recorded the
 * moment the signal came back. The server bounds it (after the check-in, not
 * in the future) and falls back to its own clock if it doesn't hold.
 */
export async function completeWorkDay(input: {
  preview?: boolean;
  expectedWorkerId?: string;
  breakReport?: WorkerBreakReport;
  jobId: string;
  timesheetId: string;
  location?: { latitude: number; longitude: number };
  endedAt?: string;
  /**
   * 'queue' when `endedAt` is the queue's creation time standing in;
   * 'chosen' when the worker picked an earlier end themselves (stage 1A,
   * B1/Q51) — the server marks such a day for review (`worker_chosen_end`).
   */
  endedAtSource?: 'tap' | 'queue' | 'chosen';
  photos?: { url: string; type: TimesheetPhoto['type'] }[];
}): Promise<{
  ok: boolean;
  status: Timesheet['status'];
  breakdown?: TimesheetHourBreakdown;
  totalHours?: number;
  autoLunch?: Timesheet['autoLunch'];
}> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    typeof input,
    {
      ok: boolean;
      status: Timesheet['status'];
      breakdown?: TimesheetHourBreakdown;
      totalHours?: number;
      autoLunch?: Timesheet['autoLunch'];
    }
  >(functions, 'completeWorkDay');
  const r = await callable(input);
  return r.data;
}

/**
 * Manually correct a check-in/out time (worker who forgot to check in on site,
 * or admin fixing the sheet). Audited server-side. Times are ISO strings.
 */
export async function adjustTimesheetTime(input: {
  jobId: string;
  timesheetId: string;
  entryIndex?: number;
  checkInAt?: string;
  checkOutAt?: string;
  reason?: string;
}): Promise<{ ok: boolean; totalHours: number }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<typeof input, { ok: boolean; totalHours: number }>(
    functions,
    'adjustTimesheetTime',
  );
  return (await callable(input)).data;
}

/**
 * Storage path for a timesheet photo, scoped to whoever is uploading it.
 *
 * The uid segment is not decoration: `firebase/storage/storage.rules` gates
 * worker writes on `request.auth.uid == uploaderUid`, since it has no way to
 * read the timesheet and learn whose it is. Taking the uid from the signed-in
 * session rather than from a caller-supplied field keeps the path and the
 * credential that must match it from ever drifting apart.
 */
function timesheetPhotoPath(jobId: string, timesheetId: string, photoId: string): string {
  const { auth } = getFirebase();
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not signed in');
  return `jobs/${jobId}/timesheets/${timesheetId}/photos/${uid}/${photoId}`;
}

/** Upload once, then attach through the owner-checked append-only server path. */
export async function uploadTimesheetPhoto(input: {
  jobId: string;
  timesheetId: string;
  file: File;
  type: TimesheetPhoto['type'];
  uploadedBy: string;
  expectedWorkerId?: string;
  photoId?: string;
}): Promise<TimesheetPhoto> {
  const { storage, functions, auth } = getFirebase();
  if (
    input.uploadedBy !== auth.currentUser?.uid ||
    (input.expectedWorkerId && input.expectedWorkerId !== auth.currentUser?.uid)
  )
    throw {
      code: 'functions/permission-denied',
      message: 'WORKER_ACCOUNT_CHANGED',
      details: { reason: 'WORKER_ACCOUNT_CHANGED' },
    };
  const expectedWorkerId = input.expectedWorkerId ?? input.uploadedBy;
  const sheet = await getDoc(
    doc(getFirebase().firestore, 'jobs', input.jobId, 'timesheets', input.timesheetId),
  );
  if (sheet.data()?.workerId !== expectedWorkerId || auth.currentUser?.uid !== expectedWorkerId)
    throw { code: 'functions/permission-denied', details: { reason: 'WORKER_ACCOUNT_CHANGED' } };
  const photoId = input.photoId ?? crypto.randomUUID();
  const objectRef = storageRef(
    storage,
    timesheetPhotoPath(input.jobId, input.timesheetId, photoId),
  );
  try {
    await getMetadata(objectRef);
  } catch (error) {
    if ((error as { code?: string }).code !== 'storage/object-not-found') throw error;
    await uploadBytes(objectRef, input.file, { contentType: input.file.type });
  }
  const call = httpsCallable<
    {
      jobId: string;
      timesheetId: string;
      photoId: string;
      type: TimesheetPhoto['type'];
      expectedWorkerId: string;
    },
    { photo: TimesheetPhoto }
  >(functions, 'appendTimesheetPhoto');
  return (
    await call({
      jobId: input.jobId,
      timesheetId: input.timesheetId,
      photoId,
      type: input.type,
      expectedWorkerId,
    })
  ).data.photo;
}

/**
 * EPIC 33 — admin/dispatcher/finance full edit of a timesheet. Sends the
 * complete edited entries array; the server diffs + logs + recomputes. For an
 * already-invoiced sheet pass `override: true` + a `reason` (flags the invoice).
 */
export async function editTimesheet(input: {
  jobId: string;
  timesheetId: string;
  entries: Timesheet['entries'];
  override?: boolean;
  reason?: string;
  /** EPIC 39 — audited admin override: cancel the statutory auto-lunch (reason required). */
  cancelAutoLunch?: boolean;
}): Promise<{ ok: boolean; totalHours: number; changed: number; postInvoice?: boolean }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    typeof input,
    { ok: boolean; totalHours: number; changed: number; postInvoice?: boolean }
  >(functions, 'editTimesheet');
  return (await callable(input)).data;
}

async function editTimesheetPhotosCallable(input: {
  jobId: string;
  timesheetId: string;
  add?: { url: string; type: TimesheetPhoto['type'] };
  removeUrl?: string;
  override?: boolean;
  reason?: string;
}): Promise<{ ok: boolean; photos: number; postInvoice?: boolean }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    typeof input,
    { ok: boolean; photos: number; postInvoice?: boolean }
  >(functions, 'editTimesheetPhotos');
  return (await callable(input)).data;
}

/** Admin adds a photo: upload to Storage, then record + log via the callable. */
export async function adminAddTimesheetPhoto(input: {
  jobId: string;
  timesheetId: string;
  file: File;
  type: TimesheetPhoto['type'];
  override?: boolean;
  reason?: string;
}): Promise<void> {
  const { storage } = getFirebase();
  const photoId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const path = timesheetPhotoPath(input.jobId, input.timesheetId, photoId);
  const objectRef = storageRef(storage, path);
  await uploadBytes(objectRef, input.file, { contentType: input.file.type });
  const url = await getDownloadURL(objectRef);
  await editTimesheetPhotosCallable({
    jobId: input.jobId,
    timesheetId: input.timesheetId,
    add: { url, type: input.type },
    override: input.override,
    reason: input.reason,
  });
}

/** Admin deletes a photo (irreversible): removes from array + Storage + logs. */
export async function adminRemoveTimesheetPhoto(input: {
  jobId: string;
  timesheetId: string;
  url: string;
  override?: boolean;
  reason?: string;
}): Promise<void> {
  await editTimesheetPhotosCallable({
    jobId: input.jobId,
    timesheetId: input.timesheetId,
    removeUrl: input.url,
    override: input.override,
    reason: input.reason,
  });
}

/**
 * Subscribe to every timesheet across all jobs filtered by status.
 * Used by /admin/timesheets queue. Worker / dispatcher / admin / finance
 * have read access via firestore.rules; client_user reads scoped via the
 * client-permissions check in the rules.
 */
/**
 * Two views that are not statuses, because the thing an admin is looking for
 * is not always a status:
 *   - `open`: a draft whose last entry has no check-out — work that is still
 *     running. Before this you could only find one through "Kõik", which
 *     streams every timesheet ever written; a day stuck at hundreds of hours
 *     was therefore easiest to discover at month end.
 *   - `review`: a day carrying an unresolved question. These are excluded
 *     from holdover auto-approval, so without somewhere to see them they
 *     would simply stop, silently, forever.
 */
export type TimesheetView = Timesheet['status'] | 'all' | 'open' | 'review' | 'action';

export function useTimesheetsByStatus(status: TimesheetView): {
  timesheets: Timesheet[];
  loading: boolean;
} {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  // "Needs action" also changes with the clock alone — a day turning 16 h
  // open, hours turning 3 days unapproved — so it is re-judged every minute,
  // not only when a document changes.
  const now = useNow();

  useEffect(() => {
    setLoading(true);
    const { firestore } = getFirebase();
    const base = collectionGroup(firestore, 'timesheets');
    if (status === 'action') return subscribeActionTimesheets(base, setTimesheets, setLoading);
    const q =
      status === 'all'
        ? base
        : status === 'review'
          ? query(base, where('needsReview', '==', true))
          : // 'open' rides the draft stream and narrows below: "last entry has
            // no check-out" is not expressible as a Firestore filter.
            query(base, where('status', '==', status === 'open' ? 'draft' : status));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map(
          (d) =>
            ({
              id: d.id,
              ...(d.data() as Omit<Timesheet, 'id'>),
              jobId: d.ref.parent.parent?.id ?? '',
            }) as Timesheet,
        );
        list.sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''));
        setTimesheets(status === 'open' ? list.filter(isActiveTimesheet) : list);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [status]);

  const judged = useMemo(
    () => (status === 'action' ? needingAction(timesheets, now) : timesheets),
    [status, timesheets, now],
  );
  return { timesheets: judged, loading };
}


/**
 * Marti 2026-10-28 — the days that need RM24 now (lib/adminActions): a
 * question, a client rejection awaiting RM24, a day left open, hours the client
 * is late approving. Two live queries merged by path; which candidates need
 * action is judged against the clock in useTimesheetsByStatus. The admin menu's number counts exactly these rows.
 */
function subscribeActionTimesheets(
  base: ReturnType<typeof collectionGroup>,
  onRows: (rows: Timesheet[]) => void,
  onLoading: (loading: boolean) => void,
): () => void {
  const parts: Array<Map<string, Timesheet> | undefined> = [undefined, undefined, undefined];
  // Candidates only: which of them need action is judged against the clock
  // in useTimesheetsByStatus.
  const publish = () => {
    if (parts.some((p) => !p)) return;
    const merged = new Map<string, Timesheet>();
    for (const p of parts) for (const [k, v] of p!) merged.set(k, v);
    onRows([...merged.values()]);
    onLoading(false);
  };
  const queries = [
    query(base, where('needsReview', '==', true)),
    query(base, where('status', 'in', ['rejected', 'submitted', 'draft'])),
    // M-D4b v4.3 — early ends awaiting RM24's decision (approved days too).
    query(base, where('shiftEndPending', '==', true)),
  ];
  const unsubs = queries.map((q, i) =>
    onSnapshot(
      q,
      (snap) => {
        parts[i] = new Map(
          snap.docs.map((d) => [
            d.ref.path,
            {
              id: d.id,
              ...(d.data() as Omit<Timesheet, 'id'>),
              jobId: d.ref.parent.parent?.id ?? '',
            } as Timesheet,
          ]),
        );
        publish();
      },
      () => onLoading(false),
    ),
  );
  return () => unsubs.forEach((u) => u());
}

/**
 * EPIC 44 Phase 2 — the live "Praegu" board's data source: every worker who is
 * clocked in right now. Rides the existing `status == 'draft'` collection-group
 * stream (single-field index already in place) and filters client-side to the
 * OPEN-entry drafts (last entry checked in, not yet out). Cheap: this is a
 * hot-set read of only open drafts — a handful of docs at MVP volume, not the
 * whole timesheet history.
 */
export function useActiveTimesheets(): { timesheets: Timesheet[]; loading: boolean } {
  const { timesheets, loading } = useTimesheetsByStatus('draft');
  const active = useMemo(() => timesheets.filter(isActiveTimesheet), [timesheets]);
  return { timesheets: active, loading };
}

/**
 * Subscribe to timesheets for the clients a foreman / B2B portal user
 * has access to. Powered by the existing collectionGroup query +
 * `clientId in permissions` rule. Foreman approval still gates on
 * Site.foremanUserId server-side — the UI listing here is broader so
 * the foreman can see why they're not allowed to act.
 */
export function useClientTimesheets(
  clientIds: string[],
  status: Timesheet['status'] | 'all',
): { timesheets: Timesheet[]; loading: boolean } {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (clientIds.length === 0) {
      setTimesheets([]);
      return;
    }
    setLoading(true);
    const { firestore } = getFirebase();
    const merged = new Map<string, Timesheet>();
    const unsubs = clientIds.map((cid) => {
      const base = collectionGroup(firestore, 'timesheets');
      const q =
        status === 'all'
          ? query(base, where('clientId', '==', cid))
          : query(base, where('clientId', '==', cid), where('status', '==', status));
      return onSnapshot(
        q,
        (snap) => {
          for (const d of snap.docs) {
            merged.set(d.ref.path, {
              id: d.id,
              ...(d.data() as Omit<Timesheet, 'id'>),
              jobId: d.ref.parent.parent?.id ?? '',
            } as Timesheet);
          }
          // Drop docs that no longer match this clientId's snap.
          const seen = new Set(snap.docs.map((d) => d.ref.path));
          for (const path of [...merged.keys()]) {
            const cidForPath = merged.get(path)?.clientId;
            if (cidForPath === cid && !seen.has(path)) {
              merged.delete(path);
            }
          }
          const arr = [...merged.values()];
          arr.sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''));
          setTimesheets(arr);
          setLoading(false);
        },
        () => setLoading(false),
      );
    });
    return () => unsubs.forEach((u) => u());
  }, [clientIds.join(','), status]);

  return { timesheets, loading };
}

/** How far back the client approval queue lists already-decided work. */
export const CLIENT_APPROVAL_HISTORY_DAYS = 90;

/**
 * PB-05 — the client approval queue's BOUNDED source. It used to stream the
 * client's whole timesheet history ('all'). Now, per company:
 *   - every sheet waiting for the client (`submitted`) and every open day
 *     (`draft`) — the work that needs or will need action, never windowed;
 *   - decided work (`approved` / `rejected` / `invoiced`) submitted within the
 *     last `historyDays` days. That query is (clientId ==, status in,
 *     submittedAt >=, orderBy submittedAt desc) — the deployed collection-group
 *     index (clientId, status, submittedAt DESC); no new index.
 * Rules are unchanged: every query is scoped by `clientId`.
 */
export function useClientApprovalTimesheets(
  clientIds: string[],
  historyDays: number = CLIENT_APPROVAL_HISTORY_DAYS,
  nowMs: number = Date.now(),
): { timesheets: Timesheet[]; loading: boolean; sinceIso: string } {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(false);
  // Fixed per mount (and per window), so the subscription does not churn.
  const sinceIso = useMemo(
    () => new Date(nowMs - historyDays * 86400000).toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [historyDays],
  );

  useEffect(() => {
    if (clientIds.length === 0) {
      setTimesheets([]);
      return;
    }
    setLoading(true);
    const { firestore } = getFirebase();
    const base = collectionGroup(firestore, 'timesheets');
    // One result map per query; the union is what the queue shows.
    const results = new Map<string, Map<string, Timesheet>>();
    const pending = new Set<string>();
    const publish = () => {
      const union = new Map<string, Timesheet>();
      for (const m of results.values()) for (const [path, ts] of m) union.set(path, ts);
      const arr = [...union.values()];
      arr.sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''));
      setTimesheets(arr);
      if (pending.size === 0) setLoading(false);
    };
    const unsubs = clientIds.flatMap((cid) => {
      const queries = [
        [
          `${cid}:submitted`,
          query(base, where('clientId', '==', cid), where('status', '==', 'submitted')),
        ],
        [`${cid}:draft`, query(base, where('clientId', '==', cid), where('status', '==', 'draft'))],
        [
          `${cid}:recent`,
          query(
            base,
            where('clientId', '==', cid),
            where('status', 'in', ['approved', 'rejected', 'invoiced']),
            where('submittedAt', '>=', sinceIso),
            orderBy('submittedAt', 'desc'),
          ),
        ],
      ] as const;
      return queries.map(([key, q]) => {
        pending.add(key);
        return onSnapshot(
          q,
          (snap) => {
            const m = new Map<string, Timesheet>();
            for (const d of snap.docs)
              m.set(d.ref.path, {
                id: d.id,
                ...(d.data() as Omit<Timesheet, 'id'>),
                jobId: d.ref.parent.parent?.id ?? '',
              } as Timesheet);
            results.set(key, m);
            pending.delete(key);
            publish();
          },
          () => {
            pending.delete(key);
            publish();
          },
        );
      });
    });
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds.join(','), sinceIso]);

  return { timesheets, loading, sinceIso };
}

/**
 * EPIC 42 — subscribe to every timesheet for ONE worker across all jobs, for
 * the worker sheet's "Töögraafik" timeline. Mirrors `subscribeMyTimesheets`
 * (worker.ts) but is parameterised by workerId: a collectionGroup('timesheets')
 * query filtered by `workerId`, readable by admin / dispatcher / finance per
 * firestore.rules. No composite index needed — the single-field collection-group
 * index on `workerId` already exists; status / date grouping is done client-side.
 */
export function useWorkerTimesheets(workerId: string | undefined): {
  timesheets: Timesheet[];
  loading: boolean;
  error: string | null;
} {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workerId) {
      setTimesheets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { firestore } = getFirebase();
    const q = query(collectionGroup(firestore, 'timesheets'), where('workerId', '==', workerId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map(
          (d) =>
            ({
              id: d.id,
              ...(d.data() as Omit<Timesheet, 'id'>),
              jobId: d.ref.parent.parent?.id ?? '',
            }) as Timesheet,
        );
        setTimesheets(list);
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      },
    );
    return unsub;
  }, [workerId]);

  return { timesheets, loading, error };
}

/** Subscribe to all timesheets for a single job (used in JobDetailPage). */
export function useJobTimesheets(jobId: string | undefined): {
  timesheets: Timesheet[];
  loading: boolean;
} {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!jobId) {
      setTimesheets([]);
      return;
    }
    setLoading(true);
    const { firestore } = getFirebase();
    const ref = collection(firestore, 'jobs', jobId, 'timesheets');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setTimesheets(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Timesheet),
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [jobId]);
  return { timesheets, loading };
}

/**
 * Report that a recorded time is wrong.
 *
 * Records the claim; changes nothing. `proposedValue` may be omitted —
 * "this is wrong, I don't remember exactly when" is a real report, and
 * demanding a precise time would either suppress it or invite an invented
 * one. An admin decides, and the original, the proposal and the decision
 * all stay on the sheet.
 */
export async function requestTimeCorrection(input: {
  requestId?: string;
  jobId: string;
  timesheetId: string;
  entryIndex: number;
  field: 'checkInAt' | 'checkOutAt';
  proposedValue?: string;
  proposedCheckInAt?: string;
  note?: string;
}): Promise<{ ok: true; correctionId: string; alreadyOpen: boolean }> {
  const { functions } = getFirebase();
  const fn = httpsCallable<typeof input, { ok: true; correctionId: string; alreadyOpen: boolean }>(
    functions,
    'requestTimeCorrection',
  );
  const { data } = await fn(input);
  return data;
}

/** Staff answering one of those claims: accept it, accept a different time, or refuse. */
export async function decideTimeCorrection(input: {
  jobId: string;
  timesheetId: string;
  correctionId: string;
  accept: boolean;
  value?: string;
  checkInValue?: string;
  note?: string;
}): Promise<{ ok: true; accepted: boolean; needsReview: boolean }> {
  const { functions } = getFirebase();
  const fn = httpsCallable<typeof input, { ok: true; accepted: boolean; needsReview: boolean }>(
    functions,
    'decideTimeCorrection',
  );
  const { data } = await fn(input);
  return data;
}

/**
 * Start a working day. The server owns the "one day at a time" rule and
 * decides whether this is a new session or one that already exists — calling
 * twice returns the SAME timesheet, so a double tap or a lost response
 * cannot open two.
 *
 * Throws `already-exists` when the worker is clocked in somewhere else; the
 * error's details carry the job and sheet that are running.
 */
export async function startWorkDay(input: {
  continueTimesheetId?: string;
  expectedWorkerId?: string;
  jobId: string;
  location?: { latitude: number; longitude: number };
  startedAt?: string;
  /** 'queue' when `startedAt` is the queue's creation time standing in. */
  startedAtSource?: 'tap' | 'queue';
  clientRequestId?: string;
}): Promise<{ ok: true; timesheetId: string; jobId: string; startedAt: string; reused: boolean }> {
  const { functions } = getFirebase();
  const fn = httpsCallable<
    typeof input,
    { ok: true; timesheetId: string; jobId: string; startedAt: string; reused: boolean }
  >(functions, 'startWorkDay');
  const { data } = await fn(input);
  return data;
}

/**
 * Record that a person has settled a day's open questions, so its hours can
 * count. Changes no times — correcting the day is `editTimesheet`.
 */
export async function resolveTimesheetReview(input: {
  jobId: string;
  timesheetId: string;
  reasons?: ('end_time_unverified' | 'unresolved_break' | 'daily_break_reconciliation')[];
  keepCurrentAccounting?: boolean;
  note: string;
  breakDecision?: WorkerBreakDecision;
}): Promise<{ ok: true; closed: number; needsReview: boolean }> {
  const { functions } = getFirebase();
  const fn = httpsCallable<typeof input, { ok: true; closed: number; needsReview: boolean }>(
    functions,
    'resolveTimesheetReview',
  );
  const { data } = await fn(input);
  return data;
}

/**
 * M-D4a — RM24 decides which ordered worker place (and shift date) a day
 * belongs to; settles an open `place_unresolved`. Refused on an approved,
 * invoiced or draft-reserved day. Staff only.
 */
export async function setTimesheetPlace(input: {
  jobId: string;
  timesheetId: string;
  placeId: string;
  assignmentId?: string | null;
  shiftDate?: string | null;
  reason: string;
}): Promise<{ ok: true; changed: boolean; place?: TimesheetPlace }> {
  const { functions } = getFirebase();
  const fn = httpsCallable<typeof input, { ok: true; changed: boolean; place?: TimesheetPlace }>(
    functions,
    'setTimesheetPlace',
  );
  const { data } = await fn(input);
  return data;
}

/**
 * M-D4b v4.3 — RM24 decides why a day ended before the ordered end:
 * `client_ended` (the minimum stands) or `provider_shortfall` (reduced).
 * Allowed until the work is invoiced or on an invoice draft. Staff only.
 */
export async function decideShiftEnd(input: {
  jobId: string;
  timesheetId: string;
  kind: 'client_ended' | 'provider_shortfall';
  note: string;
}): Promise<{ ok: true; changed: boolean }> {
  const { functions } = getFirebase();
  const fn = httpsCallable<typeof input, { ok: true; changed: boolean }>(functions, 'decideShiftEnd');
  return (await fn(input)).data;
}

/** M-D4b — the order a day belongs to, for the "Set place" dialog (its places). */
export async function fetchJobForPlace(jobId: string): Promise<Job | null> {
  const snap = await getDoc(doc(getFirebase().firestore, 'jobs', jobId));
  return snap.exists() ? ({ ...(snap.data() as Job), id: snap.id } as Job) : null;
}

// A worker no longer starts or ends breaks: their day is start and finish.
// The statutory lunch is deducted server-side at completion and an admin
// corrects an actual break in the timesheet editor, so the `toggleWorkBreak`
// wrapper that used to live here is gone along with the button that called it.
// Recorded breakPeriods stay readable everywhere they were shown.

/** Read-only server calculation, using the same prepared end as final submission. */
export function previewWorkDay(input: { jobId: string; timesheetId: string; endedAt: string }) {
  return completeWorkDay({ ...input, preview: true });
}

export async function finalizeTimesheetRejection(input: {
  jobId: string;
  timesheetId: string;
  rejectedAt: string;
  reason: string;
}): Promise<{ ok: boolean; alreadyFinal: boolean }> {
  const { functions } = getFirebase();
  return (
    await httpsCallable<typeof input, { ok: boolean; alreadyFinal: boolean }>(
      functions,
      'finalizeTimesheetRejection',
    )(input)
  ).data;
}

// ── UX stage 3 (hours → invoice) ────────────────────────────────────────────

/** The server's half of a shift card (`listClientShiftCards`): its verdicts, never re-derived. */
export interface ShiftCardFacts {
  jobId: string;
  timesheetId: string;
  /** RM24 must settle a question first; no route approves the day until then. */
  heldForReview: boolean;
  /** When the holdover sweep approves the day by itself — only where it really does. */
  autoApproveAt: string | null;
  /** What the day would bill, from the invoice's own functions; absent without the billing right. */
  money?: ShiftCardMoney;
}

export async function listClientShiftCards(input: {
  clientId: string;
  refs: { jobId: string; timesheetId: string }[];
}): Promise<ShiftCardFacts[]> {
  const { functions } = getFirebase();
  const callable = httpsCallable<typeof input, { ok: true; cards: ShiftCardFacts[] }>(
    functions,
    'listClientShiftCards',
  );
  const r = await callable(input);
  return r.data.cards;
}

/** The callable's limit per request (`ListClientShiftCardsSchema`). */
export const SHIFT_CARD_REFS_PER_CALL = 300;

/** Refs per call, keeping each order's days in one call so the server reads it once. */
function shiftCardChunks(
  refs: { jobId: string; timesheetId: string }[],
): { jobId: string; timesheetId: string }[][] {
  const byJob = new Map<string, { jobId: string; timesheetId: string }[]>();
  for (const r of refs) byJob.set(r.jobId, [...(byJob.get(r.jobId) ?? []), r]);
  const chunks: { jobId: string; timesheetId: string }[][] = [];
  let current: { jobId: string; timesheetId: string }[] = [];
  for (const list of byJob.values()) {
    if (current.length > 0 && current.length + list.length > SHIFT_CARD_REFS_PER_CALL) {
      chunks.push(current);
      current = [];
    }
    // Only an order larger than one call is split.
    let rest = list;
    while (rest.length > SHIFT_CARD_REFS_PER_CALL) {
      chunks.push(rest.slice(0, SHIFT_CARD_REFS_PER_CALL));
      rest = rest.slice(SHIFT_CARD_REFS_PER_CALL);
    }
    current.push(...rest);
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

const askedStatus = (status: string) => status === 'submitted' || status === 'approved';

/**
 * Server facts for the sheets on screen, keyed `${jobId}/${timesheetId}`.
 * Asked per company (each sheet's own `clientId`) in chunks the callable accepts.
 * A failed call leaves its sheets without facts: no "auto-approves on…" line, no
 * held marker and no amount — the server still refuses anything held, so the safe
 * failure is to promise less. One failed chunk does not lose the others, and its
 * days are asked again at the next change.
 *
 * A status change re-asks only THAT order. Every day of it, not just the changed
 * one: an approval can reconcile the lunch or settle an early end, and a dispute
 * changes its neighbours in the shift, so an earlier answer cannot be kept for
 * any of them. Other orders' answers cannot have changed and are kept.
 *
 * `allSheets` is what status changes are watched on. Pass the unfiltered list when
 * `sheets` is a filtered view: leaving a tab must not read as a change, and a
 * neighbour's change must be seen even when it is not on screen.
 */
export function useShiftCardFacts(
  sheets: Pick<Timesheet, 'id' | 'jobId' | 'status' | 'clientId'>[],
  allSheets: Pick<Timesheet, 'id' | 'jobId' | 'status' | 'clientId'>[] = sheets,
): Map<string, ShiftCardFacts> {
  const [facts, setFacts] = useState<Map<string, ShiftCardFacts>>(new Map());
  const seen = useRef(new Map<string, { jobId: string; status: string }>());
  const asked = useRef(new Set<string>());
  /** Per order: the newest ask. An older answer arriving late is dropped. */
  const round = useRef(new Map<string, number>());
  const signature = (list: typeof sheets) =>
    list
      .map((s) => `${s.clientId}/${s.jobId}/${s.id}:${s.status}`)
      .sort()
      .join(',');
  const key = `${signature(sheets)}|${signature(allSheets)}`;

  useEffect(() => {
    const keyOf = (s: { jobId: string; id: string }) => `${s.jobId}/${s.id}`;
    const now = new Map(allSheets.map((s) => [keyOf(s), { jobId: s.jobId, status: s.status }]));
    const knownJobs = new Set([...seen.current.values()].map((v) => v.jobId));
    const changedJobs = new Set<string>();
    for (const [k, v] of now) {
      const before = seen.current.get(k);
      // A new record can join a shift somebody else's amount depends on.
      if (before ? before.status !== v.status : knownJobs.has(v.jobId)) changedJobs.add(v.jobId);
    }
    for (const [k, v] of seen.current) if (!now.has(k)) changedJobs.add(v.jobId);
    seen.current = now;

    for (const jobId of changedJobs) round.current.set(jobId, (round.current.get(jobId) ?? 0) + 1);
    for (const k of [...asked.current]) if (changedJobs.has(k.slice(0, k.lastIndexOf('/')))) asked.current.delete(k);

    const leftScope = new Set(
      allSheets.filter((s) => !askedStatus(s.status)).map(keyOf),
    );
    if (leftScope.size > 0) {
      setFacts((cur) => ([...leftScope].some((k) => cur.has(k)) ? new Map([...cur].filter(([k]) => !leftScope.has(k))) : cur));
    }

    const byCompany = new Map<string, { jobId: string; timesheetId: string }[]>();
    for (const s of sheets) {
      if (!askedStatus(s.status) || !s.clientId || asked.current.has(keyOf(s))) continue;
      asked.current.add(keyOf(s));
      byCompany.set(s.clientId, [...(byCompany.get(s.clientId) ?? []), { jobId: s.jobId, timesheetId: s.id }]);
    }
    for (const [clientId, refs] of byCompany) {
      for (const chunk of shiftCardChunks(refs)) {
        const rounds = new Map(chunk.map((r) => [r.jobId, round.current.get(r.jobId) ?? 0]));
        const current = (jobId: string) => rounds.get(jobId) === (round.current.get(jobId) ?? 0);
        const refKey = (r: { jobId: string; timesheetId: string }) => `${r.jobId}/${r.timesheetId}`;
        void listClientShiftCards({ clientId, refs: chunk }).then(
          (cards) => {
            const fresh = cards.filter((c) => current(c.jobId));
            if (fresh.length === 0) return;
            setFacts((cur) => {
              const next = new Map(cur);
              for (const c of fresh) next.set(refKey(c), c);
              return next;
            });
          },
          () => {
            const failed = chunk.filter((r) => current(r.jobId)).map(refKey);
            for (const k of failed) asked.current.delete(k);
            setFacts((cur) => new Map([...cur].filter(([k]) => !failed.includes(k))));
          },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return facts;
}

/** Stage 3.4 — measured work claims on one order (`jobs/{jobId}/quantities`), waiting ones. */
export function subscribeSubmittedQuantities(
  jobId: string,
  next: (rows: JobQuantity[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    query(collection(firestore, `jobs/${jobId}/quantities`), where('status', '==', 'submitted')),
    (snap) =>
      next(snap.docs.map((d) => ({ ...(d.data() as Omit<JobQuantity, 'id'>), id: d.id }) as JobQuantity)),
    onError,
  );
}

/** Stage 3.4 — accept (fully or partly) or refuse a measured-work claim. */
export async function approveJobQuantity(input: {
  jobId: string;
  quantityId: string;
  decision: 'approve' | 'reject';
  /** Accept less than claimed; omitted = the full claim. */
  approvedQuantity?: number;
  reason?: string;
}): Promise<{ ok: true; status: 'approved' | 'rejected'; approvedQuantity: number }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    typeof input,
    { ok: true; status: 'approved' | 'rejected'; approvedQuantity: number }
  >(functions, 'approveJobQuantity');
  const r = await callable(input);
  return r.data;
}

/**
 * The words of a service claim, from the order's audit row — never the job
 * document, which assigned workers read. Null when absent or not readable.
 */
export async function getServiceClaimText(jobId: string): Promise<string | null> {
  const { firestore } = getFirebase();
  const snap = await getDoc(doc(firestore, 'jobs', jobId, 'auditLog', SERVICE_ANSWER_AUDIT_ID));
  const text = (snap.data() as { detail?: { claimText?: unknown } } | undefined)?.detail?.claimText;
  return typeof text === 'string' ? text : null;
}

/** Stage 3.4 — accept a finished fixed-price service, or file a claim (information only). */
export async function respondServiceCompletion(input: {
  jobId: string;
  decision: 'accept' | 'claim';
  claimText?: string;
}): Promise<{ ok: true; alreadyAnswered: boolean; serviceAcceptance: ServiceAcceptance }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    typeof input,
    { ok: true; alreadyAnswered: boolean; serviceAcceptance: ServiceAcceptance }
  >(functions, 'respondServiceCompletion');
  const r = await callable(input);
  return r.data;
}
